import { useState } from "react";
import Switch from "@mui/material/Switch";
import { Check, Copy } from "lucide-react";

import { Spinner } from "@/components/common/svgs";
import { useTruckListConfig, useUpdateTruckListConfig } from "@/hooks/useTruckList";

// Opt-in toggle for the carrier's public truck-list page. Reads current state via
// GET /api/v0/truck-list-config (returns null when no row yet OR the user has no
// company in backoffice — both treated as "off"). The PUT will surface a clear
// error from the server if the user lacks a company.
//
// TODO: once the backend distinguishes "user has no company" (currently 404) from
// "no config row yet" (also 404), hide the whole section for the no-company case
// — a toggle that can never succeed isn't helpful. Leaving it visible for now so
// the enable flow can be exercised against a fresh dispatcher account.
export const TruckListToggle = () => {
    const { data: config } = useTruckListConfig();
    const mutation = useUpdateTruckListConfig();
    const [copied, setCopied] = useState(false);

    const confirmedEnabled = !!config?.enabled;
    // Optimistic display: while a PUT is in flight, show the value the user just
    // clicked instead of the previously-confirmed one. Makes the toggle feel
    // responsive and prevents the controlled <Switch> from visually snapping back
    // to the old position while we wait for the server. On error the cache stays
    // unchanged, so the toggle reverts as soon as `isPending` flips false.
    const pendingValue = mutation.isPending && typeof mutation.variables === "boolean" ? mutation.variables : null;
    const enabled = pendingValue ?? confirmedEnabled;

    const slug = config?.slug ?? null;
    // Build the shareable URL against the current origin — the public page is
    // served from the same web app at /trucklist/:slug.
    const publicUrl = slug ? `${window.location.origin}/trucklist/${slug}` : null;

    const handleToggle = (event) => {
        mutation.mutate(event.target.checked);
        setCopied(false);
    };

    const handleCopy = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access can be denied in some browsers; fail silently rather
            // than throwing a UI error for a non-critical convenience action.
        }
    };

    return (
        <div className="flex flex-col pt-2">
            <div className="flex items-center gap-4">
                <Switch
                    className="cm-toggle cm-toggle-loads"
                    checked={enabled}
                    // TEMP: keep the toggle clickable regardless of GET state so the PUT can be
                    // exercised even when /truck-list-config returns 404. Restore the
                    // `disabled={isLoading || mutation.isPending}` guard once the GET reliably
                    // returns a config for the test account.
                    onChange={handleToggle}
                />
                <span className="text-sm font-medium text-cm-p-text-primary">{enabled ? "Enabled" : "Disabled"}</span>
                {mutation.isPending && <Spinner className="h-4 w-4 text-cm-p-text-secondary" />}
            </div>

            {enabled && publicUrl && (
                <div className="mt-3 flex h-10 w-full items-center gap-2 rounded-[20px] border border-cm-p-outline bg-cm-p-surface-container pl-4 pr-1">
                    <a href={publicUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-cm-p-accent hover:underline">
                        {publicUrl}
                    </a>
                    <button
                        type="button"
                        onClick={handleCopy}
                        aria-label="Copy public truck-list URL"
                        title={copied ? "Copied" : "Copy"}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-cm-p-text-secondary transition-colors hover:bg-cm-p-surface-container-hover hover:text-cm-p-text-primary"
                    >
                        {copied ? <Check className="h-4 w-4 text-cm-p-accent" /> : <Copy className="h-4 w-4" />}
                    </button>
                </div>
            )}

            {mutation.isError && (
                <div className="mt-2 text-cm-error text-sm">
                    {(typeof mutation.error === "string" && mutation.error) || "Couldn't update — please try again."}
                </div>
            )}
        </div>
    );
};
